import React, {useEffect, useState} from 'react';
import {Alert, Badge, Button, Container, Form, Modal, Spinner, Tab, Tabs} from 'react-bootstrap';
import {useSelector} from 'react-redux';
import {Redirect} from 'react-router-dom';
import {ROLES} from '../constants';
import CommonMetaTag from './common-meta-tag';
import AdminP2pField from './admin-p2p-field';
import {getP2pCatalog, getP2pDevices, saveP2pProfile, p2pErrorMessage} from '../api/p2p';
import '../css/admin-p2p.css';

const statusLabel = profile => profile.status === 'experimental' ? 'In prova' : 'Da verificare';
const capabilityLabel = value => value === 'tested' ? 'Verificata' : value === 'sample_tested' ? 'Campione verificato' : 'Da collaudare';

export function AdminP2pContent({token}) {
  const [catalog, setCatalog] = useState(null);
  const [devices, setDevices] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [edit, setEdit] = useState(null);
  const [editError, setEditError] = useState('');
  const [busy, setBusy] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [filter, setFilter] = useState('');
  useEffect(() => {
    let disposed = false;
    setLoading(true);
    setError('');
    Promise.all([getP2pCatalog(token), getP2pDevices(token)]).then(([models, inventory]) => {
      if (disposed) return;
      setCatalog(models);
      setDevices(inventory.devices);
    }).catch(err => { if (!disposed) setError(p2pErrorMessage(err)); })
      .finally(() => { if (!disposed) setLoading(false); });
    return () => { disposed = true; };
  }, [token, reload]);

  const openProfile = profile => { setEdit({...profile}); setEditError(''); };
  const submitProfile = async event => {
    event.preventDefault(); setBusy(true); setEditError('');
    const {manufacturer, model, adapterId, enabled, revision} = edit;
    try {
      await saveP2pProfile(edit.id, {manufacturer, model, adapterId, enabled, ...(edit.id ? {revision} : {})}, token);
      setEdit(null); setReload(n => n + 1);
    } catch (err) { setEditError(p2pErrorMessage(err)); }
    finally { setBusy(false); }
  };
  const shownDevices = devices.filter(device => `${device.partnerName} ${device.fieldName} ${device.cameraName} ${device.model}`.toLowerCase().includes(filter.toLowerCase()));
  return <Container className="p2p-admin-page">
    <CommonMetaTag/>
    <div className="p2p-page-heading"><div><p className="p2p-eyebrow">GESTIONE TELECAMERE</p><h1>P2P</h1>
      <p>Modelli disponibili e collegamenti ai campi dei partner.</p></div>
      <Button onClick={() => openProfile({manufacturer: '', model: '', adapterId: 'reolink_baichuan_p2p', enabled: true})} disabled={!catalog}>Aggiungi modello</Button>
    </div>
    {loading && <p role="status"><Spinner as="span" animation="border" size="sm"/> Caricamento…</p>}
    {error && <Alert variant="danger" role="alert">{error} <Button variant="link" onClick={() => setReload(n => n + 1)}>Riprova</Button></Alert>}
    {catalog && <>
      <Alert variant="light" className="p2p-overview">Il flag P2P si trova in <strong>Gestione partner → Campi</strong>. I campi senza flag continuano a usare l’NVR locale.</Alert>
      <Tabs defaultActiveKey="models" id="p2p-admin-tabs" className="mb-4">
        <Tab eventKey="models" title={`Modelli (${catalog.profiles.length})`}>
          <div className="p2p-model-grid">{catalog.profiles.map(profile => <article className="p2p-model-card" key={profile.id}>
            <div className="p2p-card-top"><span className="p2p-eyebrow">{profile.manufacturer}</span><Badge variant={profile.status === 'planned' ? 'secondary' : 'warning'}>{statusLabel(profile)}</Badge></div>
            <h2>{profile.model}</h2><p className="p2p-help">{profile.configurable ? 'Configurazione disponibile per le prove.' : 'Collegamento da integrare e collaudare.'}</p>
            {profile.testedFirmware && <p className="p2p-help">Prove eseguite su {profile.testedHardware}, firmware {profile.testedFirmware}.</p>}
            <dl className="p2p-capabilities"><div><dt>Diretta</dt><dd>{capabilityLabel(profile.capabilities.live)}</dd></div>
              <div><dt>Lettura SD</dt><dd>{capabilityLabel(profile.capabilities.sdRead)}</dd></div>
              <div><dt>Recupero automatico</dt><dd>{capabilityLabel(profile.capabilities.automaticRecovery)}</dd></div></dl>
            <div className="p2p-card-bottom"><span>{profile.enabled && profile.configurable ? 'Selezionabile nei campi' : 'Non selezionabile'}</span>
              <Button variant="outline-secondary" size="sm" onClick={() => openProfile(profile)}>Modifica</Button></div>
          </article>)}</div>
        </Tab>
        <Tab eventKey="devices" title={`Telecamere configurate (${devices.length})`}>
          <Form.Group controlId="p2p-search"><Form.Label>Cerca partner, campo o telecamera</Form.Label><Form.Control type="search" value={filter} onChange={event => setFilter(event.target.value)}/></Form.Group>
          {shownDevices.length === 0 ? <div className="p2p-empty"><h2>{devices.length ? 'Nessun risultato' : 'Nessuna telecamera configurata'}</h2><p>Apri un campo nella gestione partner e attiva il flag P2P per inserire i dati della camera.</p></div> :
            <div className="p2p-device-list">{shownDevices.map(device => <article className="p2p-device-row" key={device.id}>
              <div><strong>{device.cameraName}</strong><p>{device.partnerName} · {device.fieldName}</p><small>{device.model}</small></div>
              <Badge variant={device.active ? 'success' : device.enabled ? 'warning' : 'light'}>{device.active ? 'P2P attivo' : device.enabled ? 'Da attivare' : 'NVR locale'}</Badge>
              <Button variant="outline-secondary" size="sm" onClick={() => setSelectedDevice(device)}>Configura campo</Button>
            </article>)}</div>}
        </Tab>
      </Tabs>
    </>}
    <Modal className="p2p-admin-modal" show={!!edit} onHide={() => !busy && setEdit(null)} centered>
      <Modal.Header closeButton><Modal.Title>{edit && edit.id ? 'Modifica modello' : 'Aggiungi modello P2P'}</Modal.Title></Modal.Header>
      <Modal.Body>{edit && <Form onSubmit={submitProfile}>
        {editError && <Alert variant="danger" role="alert">{editError}</Alert>}
        <fieldset disabled={busy}>
          <Form.Group controlId="p2p-profile-brand"><Form.Label>Marca</Form.Label><Form.Control required maxLength={80} value={edit.manufacturer} onChange={e => setEdit({...edit, manufacturer: e.target.value})}/></Form.Group>
          <Form.Group controlId="p2p-profile-model"><Form.Label>Modello</Form.Label><Form.Control required maxLength={120} value={edit.model} onChange={e => setEdit({...edit, model: e.target.value})}/></Form.Group>
          <Form.Group controlId="p2p-profile-adapter"><Form.Label>Tipo di collegamento</Form.Label><Form.Control as="select" value={edit.adapterId} onChange={e => setEdit({...edit, adapterId: e.target.value})}>
            {(catalog ? catalog.adapters : []).map(adapter => <option key={adapter.id} value={adapter.id}>{adapter.label}{adapter.configurable ? ' — in prova' : ' — da integrare'}</option>)}
          </Form.Control></Form.Group>
          <Form.Check id="p2p-profile-enabled" className="mb-3" label="Abilitato nel catalogo" checked={edit.enabled} onChange={e => setEdit({...edit, enabled: e.target.checked})}/>
          <p className="p2p-help">Il modello usa il collegamento selezionato. Le funzioni vanno verificate sul modello e sul firmware effettivi prima dell’attivazione.</p>
          <Button type="submit">{busy ? 'Salvataggio…' : 'Salva modello'}</Button>
        </fieldset>
      </Form>}</Modal.Body>
    </Modal>
    <Modal className="p2p-admin-modal" show={!!selectedDevice} onHide={() => { setSelectedDevice(null); setReload(n => n + 1); }} size="lg" centered>
      <Modal.Header closeButton><Modal.Title>{selectedDevice && selectedDevice.fieldName}</Modal.Title></Modal.Header>
      <Modal.Body>{selectedDevice && <AdminP2pField key={selectedDevice.fieldId} fieldId={selectedDevice.fieldId} token={token}/>}</Modal.Body>
    </Modal>
  </Container>;
}

export default function AdminP2p() {
  const {token, profile} = useSelector(state => state.login);
  if (!token) return <Redirect to="/login"/>;
  if (!profile) return <p className="p2p-admin-page" role="status">Caricamento profilo…</p>;
  if (!profile.user || profile.user.role !== ROLES.admin) return <Redirect to="/"/>;
  return <AdminP2pContent token={token}/>;
}
